import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import {
  expenseDetailsDefaultValues,
  ExpenseDetailsFormFields,
} from '../forms/ExpenseDetails';
import {
  projectInformationDefaultValues,
  ProjectInformationFormFields,
} from '../forms/ProjectInformation';
import {
  projectFinancialsDefaultValues,
  ProjectFinancialsFormFields,
} from '@/pages/projects/expenses/forms/ProjectFinancials';

interface ExpenseFormBlurredValuesStore {
  expenseDetailValues: ExpenseDetailsFormFields;
  projectInformationValues: ProjectInformationFormFields;
  projectFinancialsValues: ProjectFinancialsFormFields;
  setExpenseDetailValues: (values: Partial<ExpenseDetailsFormFields>) => void;
  setProjectInformationValues: (
    values: Partial<ProjectInformationFormFields>,
  ) => void;
  setProjectFinancialsValues: (
    values: Partial<ProjectFinancialsFormFields>,
  ) => void;
  reset: () => void;
}

/*
 * This store is used for storing the blurred values of the expense form UI to allow for linking child components
 * and inter-child form fields with the same onBlur logic as used in a single form group.
 *
 * We need this as separate state as the Tanstack Form instance update onChanges, also for e.g. Textfields which we format and update linked fields only onBlur.
 *
 * Remember: Always reset the store mounting and unmounting the form page to avoid stale state!
 * Remember: For edit pages, we manually need to set the initial values into the store on load
 *           to have the store in sync with the Tanstack Form instance!
 * Remember: For e.g. Select fields, it is often still easier to just pass the form value down as props from the form instance,
 *           instead of making sure the state of those is in sync in the store, I recommend using this approach only when onBlur behavior needed.
 */
export const useExpenseFormBlurredValuesStore =
  create<ExpenseFormBlurredValuesStore>()(
    devtools((set) => ({
      expenseDetailValues: {} as ExpenseDetailsFormFields,
      projectInformationValues: {} as ProjectInformationFormFields,
      projectFinancialsValues: {} as ProjectFinancialsFormFields,
      setExpenseDetailValues: (values) =>
        set((state) => ({
          expenseDetailValues: { ...state.expenseDetailValues, ...values },
        })),

      setProjectInformationValues: (values) =>
        set((state) => ({
          projectInformationValues: {
            ...state.projectInformationValues,
            ...values,
          },
        })),

      setProjectFinancialsValues: (values) =>
        set((state) => ({
          projectFinancialsValues: {
            ...state.projectFinancialsValues,
            ...values,
          },
        })),
      reset: () =>
        set({
          expenseDetailValues: expenseDetailsDefaultValues,
          projectInformationValues: projectInformationDefaultValues,
          projectFinancialsValues: projectFinancialsDefaultValues,
        }),
    })),
  );
